import { useState, useEffect } from "react"
import { supabase } from "@/integrations/supabase/client"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Download, Trash2, Play, Pause, Globe, Music, MicOff, AlertCircle, Sparkles, CheckSquare, RefreshCcw } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import { ShareToggle } from "./ShareToggle"
import { GallerySelectionBar } from "./GallerySelectionBar"
import { ClickablePrompt } from "./ClickablePrompt"
import { useGallerySelection } from "@/hooks/useGallerySelection"
import { DeleteConfirmDialog } from "./DeleteConfirmDialog"

// Model display name mapping
const MUSIC_MODEL_NAMES: Record<string, string> = {
  "chirp-v5": "Suno v5",
  "V5": "Suno v5",
  "chirp-v4": "Suno v4",
  "V4": "Suno v4",
};

interface MusicTrack {
  id: string
  prompt: string
  status: string
  audio_url: string | null
  stored_audio_url: string | null
  title: string | null
  duration: number | null
  credits_used: number
  is_public: boolean
  suno_task_id: string | null
  created_at: string
  user_id: string
  mode: string | null
  model: string | null
  tags: string | null
  instrumental: boolean | null
  image_url: string | null
  error_message?: string | null
  variant_number?: number | null
  parent_generation_id?: string | null
  creator_username?: string | null
}

interface MusicGalleryProps {
  user: any
  refreshTrigger?: number
  onModelClick?: (model: string) => void
  onRetry?: (prompt: string, settings: any) => void
}

export function MusicGallery({ user, refreshTrigger, onModelClick, onRetry }: MusicGalleryProps) {
  const [tracks, setTracks] = useState<MusicTrack[]>([])
  const [loading, setLoading] = useState(true)
  const [displayCount, setDisplayCount] = useState(12)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [audioRef, setAudioRef] = useState<HTMLAudioElement | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const { toast } = useToast()

  const fetchTracks = async () => {
    try {
      let data: MusicTrack[] | null = null
      let error: any = null

      if (user) {
        // Authenticated users see their own tracks
        const result = await supabase
          .from("generated_music")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
        data = result.data
        error = result.error
      } else {
        // Anonymous users see public tracks via the sanitized view
        const result = await supabase
          .from("public_music")
          .select("*")
          .eq("status", "completed")
          .order("created_at", { ascending: false })
        // Map public_music view data to MusicTrack format (view excludes user_id and suno_task_id)
        data = (result.data || []).map(track => ({
          ...track,
          user_id: '',
          suno_task_id: null,
          is_public: true,
        })) as MusicTrack[]
        error = result.error
      }

      if (error) throw error
      setTracks(data || [])
    } catch (error) {
      console.error("Error fetching music:", error)
      toast({
        title: "Error",
        description: "Failed to load music tracks",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Poll for processing tracks
  useEffect(() => {
    if (!user) return

    const processingTracks = tracks.filter(t => t.status === "processing" && t.suno_task_id)
    if (processingTracks.length === 0) return

    const pollInterval = setInterval(async () => {
      for (const track of processingTracks) {
        try {
          const { data, error } = await supabase.functions.invoke("generate-music", {
            body: { taskId: track.suno_task_id, prompt: track.prompt },
          })

          if (error) {
            console.error("Polling error:", error)
            continue
          }

          if (data?.data?.status === "completed" || data?.data?.status === "failed") {
            fetchTracks() // Refresh to get updated data
          }
        } catch (err) {
          console.error("Polling error:", err)
        }
      }
    }, 5000)

    return () => clearInterval(pollInterval)
  }, [tracks, user])

  useEffect(() => {
    fetchTracks()
  }, [user, refreshTrigger])

  const handlePlay = (track: MusicTrack) => {
    // Prefer stored URL over external URL
    const audioUrl = track.stored_audio_url || track.audio_url
    if (!audioUrl) return

    if (playingId === track.id && audioRef) {
      audioRef.pause()
      setPlayingId(null)
      return
    }

    if (audioRef) {
      audioRef.pause()
    }

    const audio = new Audio(audioUrl)
    audio.play()
    audio.onended = () => setPlayingId(null)
    setAudioRef(audio)
    setPlayingId(track.id)
  }

  const handleDownload = async (track: MusicTrack) => {
    // Prefer stored URL over external URL
    const audioUrl = track.stored_audio_url || track.audio_url
    if (!audioUrl) return

    try {
      toast({ title: "Downloading...", description: "Please wait..." })

      const response = await fetch(audioUrl)
      if (!response.ok) throw new Error("Failed to fetch audio")

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.style.display = "none"
      a.href = url
      a.download = `${(track.title || track.prompt).slice(0, 30).replace(/[^a-z0-9]/gi, "_").toLowerCase()}.mp3`
      document.body.appendChild(a)
      a.click()

      setTimeout(() => {
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      }, 100)

      toast({ title: "Downloaded", description: "Music downloaded successfully" })
    } catch (error) {
      console.error("Download error:", error)
      window.open(audioUrl, "_blank")
      toast({
        title: "Download",
        description: "If download didn't start, the file opened in a new tab",
      })
    }
  }

  const performDelete = async (trackId: string) => {
    try {
      const { error } = await supabase
        .from("generated_music")
        .delete()
        .eq("id", trackId)

      if (error) throw error

      setTracks(tracks.filter(t => t.id !== trackId))
      toast({ title: "Deleted", description: "Track deleted successfully" })
    } catch (error) {
      console.error("Delete error:", error)
      toast({
        title: "Error",
        description: "Failed to delete track",
        variant: "destructive",
      })
      throw error
    }
  }

  const handleDelete = (trackId: string) => {
    setDeleteConfirmId(trackId)
  }

  const confirmSingleDelete = async () => {
    if (!deleteConfirmId) return
    setIsDeleting(true)
    try {
      await performDelete(deleteConfirmId)
    } finally {
      setIsDeleting(false)
      setDeleteConfirmId(null)
    }
  }

  // Gallery selection hook
  const {
    selectedItems,
    selectionMode,
    isBulkDeleting,
    isBulkDownloading,
    toggleSelection,
    selectAll,
    deselectAll,
    cancelSelection,
    startSelection,
    bulkDelete,
    bulkDownload,
    showBulkDeleteConfirm,
    setShowBulkDeleteConfirm,
    confirmBulkDelete,
    selectedCount,
  } = useGallerySelection({
    items: tracks.filter(t => t.status === 'completed' && (t.stored_audio_url || t.audio_url)),
    type: 'music',
    onDelete: performDelete,
    onRefresh: () => setTracks(prev => [...prev]),
  });

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-4">
              <div className="h-24 bg-muted rounded-lg mb-3"></div>
              <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-muted rounded w-1/2"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (tracks.length === 0) {
    return (
      <div className="text-center py-12">
        <Music className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground mb-2">
          {user ? "No music tracks generated yet" : "No public music tracks available"}
        </p>
        {user && (
          <p className="text-sm text-muted-foreground">Create your first AI music track above!</p>
        )}
      </div>
    )
  }

  // Group tracks: parent tracks with their variants
  // Filter out child variants from main list - they'll be shown within parent cards
  const parentTracks = tracks.filter(t => !t.parent_generation_id)
  const displayedTracks = parentTracks.slice(0, displayCount)
  const hasMore = parentTracks.length > displayCount

  // Get variants for a parent track
  const getVariants = (parentId: string) => {
    return tracks.filter(t => t.parent_generation_id === parentId)
  }

  return (
    <div>
      {/* Selection header */}
      {user && !selectionMode && tracks.some(t => t.status === 'completed') && (
        <div className="flex justify-end mb-4">
          <Button variant="outline" size="sm" onClick={startSelection}>
            <CheckSquare className="h-4 w-4 mr-2" />
            Select
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedTracks.map((track) => {
          const variants = getVariants(track.id)
          const allVersions = [track, ...variants]
          
          return (
            <Card key={track.id} className={`group overflow-hidden relative ${selectionMode && selectedItems.has(track.id) ? 'ring-2 ring-primary' : ''}`}>
              {/* Selection checkbox */}
              {selectionMode && track.status === 'completed' && (track.stored_audio_url || track.audio_url) && (
                <div 
                  className="absolute top-2 left-2 z-30"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelection(track.id);
                  }}
                >
                  <Checkbox
                    checked={selectedItems.has(track.id)}
                    className="h-5 w-5 bg-white/90 border-2"
                  />
                </div>
              )}
              <CardContent className="p-4">
                {/* Cover Image or Gradient */}
                <div 
                  className="relative h-32 rounded-lg mb-3 flex items-center justify-center bg-cover bg-center"
                  style={track.image_url ? { backgroundImage: `url(${track.image_url})` } : undefined}
                >
                  {!track.image_url && (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg" />
                  )}
                  
                  {track.status === "processing" && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                      <div className="text-center text-white">
                        <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full mx-auto mb-2"></div>
                        <p className="text-xs">Generating...</p>
                      </div>
                    </div>
                  )}

                  {track.status === "failed" && (
                    <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center rounded-lg p-3">
                      <AlertCircle className="h-6 w-6 text-destructive mb-1" />
                      <p className="text-white text-xs font-medium">Failed</p>
                      {track.error_message && (
                        <p className="text-white/70 text-[10px] text-center line-clamp-2 mt-1 max-w-[150px]">
                          {track.error_message}
                        </p>
                      )}
                      <p className="text-green-400 text-[10px] mt-1 mb-2">Credits refunded</p>
                      {user && track.user_id === user.id && (
                        <div className="flex gap-1">
                          {onRetry && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                onRetry(track.prompt, { model: track.model, mode: track.mode, instrumental: track.instrumental });
                                handleDelete(track.id);
                              }}
                              className="h-6 text-[10px] px-2"
                            >
                              <RefreshCcw className="h-3 w-3 mr-1" />
                              Retry
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(track.id)}
                            className="h-6 text-[10px] px-2 text-white hover:bg-white/20"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Always visible play buttons for completed tracks */}
                  {track.status === "completed" && allVersions.length > 0 && (
                    <div className="absolute inset-0 flex items-center justify-center gap-3">
                      {allVersions.map((version, idx) => {
                        const hasAudio = version.stored_audio_url || version.audio_url
                        if (!hasAudio) return null
                        
                        return (
                          <button
                            key={version.id}
                            onClick={() => handlePlay(version)}
                            className={`
                              flex items-center justify-center rounded-full transition-all
                              ${allVersions.length > 1 ? 'w-14 h-14' : 'w-16 h-16'}
                              ${playingId === version.id 
                                ? 'bg-primary text-primary-foreground scale-110' 
                                : 'bg-black/60 text-white hover:bg-black/80 hover:scale-105'}
                              backdrop-blur-sm shadow-lg
                            `}
                          >
                            {playingId === version.id ? (
                              <Pause className="h-6 w-6" />
                            ) : (
                              <div className="flex flex-col items-center">
                                <Play className="h-6 w-6" />
                                {allVersions.length > 1 && (
                                  <span className="text-[10px] font-medium mt-0.5">
                                    {String.fromCharCode(65 + idx)}
                                  </span>
                                )}
                              </div>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )}

                </div>

                {/* Badge row - consistent with VideoGallery */}
                <div className="flex gap-1 flex-wrap mb-2">
                  {track.status !== "completed" && (
                    <Badge variant={track.status === "processing" ? "secondary" : "destructive"} className="text-xs">
                      {track.status}
                    </Badge>
                  )}
                  {allVersions.length > 1 && track.status === "completed" && (
                    <Badge variant="outline" className="text-xs bg-primary/20 text-primary border-primary/50">
                      {allVersions.length} versions
                    </Badge>
                  )}
                  {track.mode && track.mode !== "simple" && (
                    <Badge variant="outline" className="text-xs bg-purple-500/20 text-purple-300 border-purple-500/50">
                      {track.mode}
                    </Badge>
                  )}
                  {track.instrumental && (
                    <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-500 border-blue-500/50">
                      <MicOff className="h-3 w-3 mr-1" />
                      Inst
                    </Badge>
                  )}
                  {track.is_public && (
                    <Badge variant="outline" className="text-xs bg-green-500/20 text-green-500 border-green-500/50">
                      <Globe className="h-3 w-3 mr-1" />
                      Public
                    </Badge>
                  )}
                  {track.model && (
                    <Badge
                      variant="outline"
                      className={`text-xs bg-amber-500/20 text-amber-500 border-amber-500/50 ${onModelClick ? 'cursor-pointer hover:bg-amber-500/30 transition-colors' : ''}`}
                      onClick={() => onModelClick && onModelClick(track.model!)}
                      title={onModelClick ? "Try this model" : undefined}
                    >
                      <Sparkles className="h-3 w-3 mr-1" />
                      {MUSIC_MODEL_NAMES[track.model] || track.model}
                    </Badge>
                  )}
                </div>

                {/* Title */}
                <ClickablePrompt 
                  text={track.title || track.prompt} 
                  tab="music"
                  className="text-sm font-medium line-clamp-2 mb-1 pr-4"
                />

                {/* Tags */}
                {track.tags && (
                  <p className="text-xs text-muted-foreground line-clamp-1 mb-2">
                    {track.tags}
                  </p>
                )}
                
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{track.credits_used} credits</span>
                  <span>
                    {track.is_public && track.creator_username && (
                      <span className="font-medium text-foreground">{track.creator_username} • </span>
                    )}
                    {new Date(track.created_at).toLocaleDateString()}
                  </span>
                </div>

                {track.status === "completed" && (
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                    {user && track.user_id === user.id && (
                      <ShareToggle
                        id={track.id}
                        isPublic={track.is_public}
                        type="music"
                        prompt={track.prompt}
                        onToggle={(newState) => {
                          setTracks(tracks.map(t =>
                            t.id === track.id ? { ...t, is_public: newState } : t
                          ))
                        }}
                      />
                    )}
                    {allVersions.map((version, idx) => {
                      const hasAudio = version.stored_audio_url || version.audio_url;
                      if (!hasAudio) return null;

                      return (
                        <Button
                          key={version.id}
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(version)}
                          className="h-8 px-2 gap-1"
                          title={allVersions.length > 1 ? `Download version ${String.fromCharCode(65 + idx)}` : "Download"}
                        >
                          <Download className="h-4 w-4" />
                          {allVersions.length > 1 && (
                            <span className="text-xs font-medium">{String.fromCharCode(65 + idx)}</span>
                          )}
                        </Button>
                      );
                    })}
                    {user && track.user_id === user.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(track.id)}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )
        })}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <Button
            onClick={() => setDisplayCount(prev => prev + 12)}
            variant="outline"
          >
            Show More
          </Button>
        </div>
      )}

      {/* Bulk action bar */}
      {selectionMode && (
        <GallerySelectionBar
          selectedCount={selectedItems.size}
          totalCount={tracks.filter(t => t.status === 'completed').length}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onCancel={cancelSelection}
          onDelete={bulkDelete}
          onDownload={bulkDownload}
          isDeleting={isBulkDeleting}
          isDownloading={isBulkDownloading}
        />
      )}

      {/* Single delete confirmation dialog */}
      <DeleteConfirmDialog
        open={!!deleteConfirmId}
        onOpenChange={(open) => !open && setDeleteConfirmId(null)}
        onConfirm={confirmSingleDelete}
        title="Delete this track?"
        description="This action cannot be undone."
        isDeleting={isDeleting}
      />

      {/* Bulk delete confirmation dialog */}
      <DeleteConfirmDialog
        open={showBulkDeleteConfirm}
        onOpenChange={setShowBulkDeleteConfirm}
        onConfirm={confirmBulkDelete}
        title={`Delete ${selectedCount} track${selectedCount > 1 ? 's' : ''}?`}
        description="This action cannot be undone."
        isDeleting={isBulkDeleting}
      />
    </div>
  )
}